import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import roomStyles from '../Room.module.css';
import styles from './OpenSourceRoom.module.css';

type OpenSourceProject = 'conduit' | 'next-phish';
type OpenSourceRoomProps = { sectionRef: RefObject<HTMLElement | null>; active: boolean };

export function OpenSourceRoom({ sectionRef, active }: OpenSourceRoomProps) {
  const sourceHintTimerRef = useRef<number | null>(null);
  const sourceInteractedRef = useRef(false);
  const [project, setProject] = useState<OpenSourceProject>('conduit');
  const [revision, setRevision] = useState(0);
  const [hintVisible, setHintVisible] = useState(false);
  const [zoomed, setZoomed] = useState(false);

  useEffect(() => {
    if (sourceHintTimerRef.current !== null) window.clearTimeout(sourceHintTimerRef.current);
    if (!active) {
      sourceInteractedRef.current = false;
      const resetTimer = window.setTimeout(() => {
        setHintVisible(false);
        setZoomed(false);
      }, 0);
      return () => window.clearTimeout(resetTimer);
    }
    sourceHintTimerRef.current = window.setTimeout(() => {
      if (!sourceInteractedRef.current) setHintVisible(true);
      sourceHintTimerRef.current = null;
    }, 4800);
    return () => {
      if (sourceHintTimerRef.current !== null) window.clearTimeout(sourceHintTimerRef.current);
    };
  }, [active, revision]);

  const markInteraction = () => {
    sourceInteractedRef.current = true;
    setHintVisible(false);
    if (sourceHintTimerRef.current !== null) window.clearTimeout(sourceHintTimerRef.current);
  };

  const selectProject = (nextProject: OpenSourceProject) => {
    markInteraction();
    setZoomed(true);
    setProject(nextProject);
    setRevision((current) => current + 1);
  };

  return (
    <section
      ref={sectionRef}
      className={`${roomStyles.room} ${roomStyles.wideRoom} ${styles.room} ${active ? styles.roomActive : ''}`}
      aria-label="Interactive pixel-art open-source kitchen lab"
    >
      <div className={`${roomStyles.scene} ${styles.scene} ${zoomed ? styles.sceneFocused : ''}`}>
        <picture className={roomStyles.background}>
          <source media="(max-width: 680px)" srcSet="/assets/v5-room-open-source-mobile.png" />
          <source media="(min-width: 681px)" srcSet="/assets/v5-room-open-source-ultrawide.png" />
          <img
            src="/assets/v5-room-open-source.png"
            alt="Warm nighttime pixel-art kitchen with an empty wooden table"
          />
        </picture>
        <div className={`${styles.sourceComputer} ${zoomed ? styles.sourceComputerFocused : ''}`}>
          <img
            src="/assets/v5-open-source-computer-front.png"
            width={1312}
            height={1199}
            alt="Front-facing 1980s portable computer with a large green CRT screen"
          />
          <button
            type="button"
            className={styles.computerTrigger}
            onClick={() => {
              markInteraction();
              setZoomed((current) => !current);
            }}
            aria-label="Focus the open-source computer screen"
            aria-pressed={zoomed}
          />
          <div className={styles.sourceTerminal} aria-live="polite">
            <nav className={styles.sourceTabs} aria-label="Open-source projects">
              <button
                type="button"
                className={project === 'conduit' ? styles.sourceTabActive : ''}
                onClick={() => selectProject('conduit')}
                aria-pressed={project === 'conduit'}
              >
                01 CONDUIT
              </button>
              <button
                type="button"
                className={project === 'next-phish' ? styles.sourceTabActive : ''}
                onClick={() => selectProject('next-phish')}
                aria-pressed={project === 'next-phish'}
              >
                02 NEXT-PHISH
              </button>
            </nav>
            {project === 'conduit' ? (
              <div key={`conduit-${revision}`} className={styles.terminalPanel}>
                <header>
                  <b>CONDUIT</b>
                  <span>RUN 0042</span>
                </header>
                <p>SPEC-DRIVEN AGENT ORCHESTRATION</p>
                <div className={styles.agentPipeline} aria-hidden="true">
                  {['SPEC', 'ARCH', 'WORK', 'QA', 'MERGE'].map((stage, index) => (
                    <div
                      key={stage}
                      style={{ '--agent-delay': `${index * 0.42}s` } as CSSProperties}
                    >
                      <i />
                      <span>{stage}</span>
                    </div>
                  ))}
                </div>
                <small>4 AGENTS · ISOLATED WORKTREES · LIVE REVIEW</small>
                <a href="https://github.com/MartinAndreev/conduit" target="_blank" rel="noreferrer">
                  VIEW SOURCE [ENTER]
                </a>
              </div>
            ) : (
              <div
                key={`phish-${revision}`}
                className={`${styles.terminalPanel} ${styles.phishTerminal}`}
              >
                <header>
                  <b>NEXT-PHISH</b>
                  <span>CAMPAIGN 07</span>
                </header>
                <p>INTERNAL SECURITY SIMULATION</p>
                <strong>PHISHING CAMPAIGN READY</strong>
                <div className={styles.campaignFlow} aria-hidden="true">
                  {['TEMPLATE', 'QUEUE', 'SEND', 'TRACK', 'REPORT'].map((stage, index) => (
                    <span
                      key={stage}
                      style={{ '--flow-delay': `${index * 0.2}s` } as CSSProperties}
                    >
                      {stage}
                      <i />
                    </span>
                  ))}
                </div>
                <small>SIMULATE · MEASURE · IMPROVE AWARENESS</small>
                <a
                  href="https://github.com/MartinAndreev/next-phish"
                  target="_blank"
                  rel="noreferrer"
                >
                  OPEN REPOSITORY [ENTER]
                </a>
              </div>
            )}
          </div>
        </div>
        <img
          className={styles.mailStack}
          src="/assets/v5-mail-stack.png"
          width={1195}
          height={620}
          alt="Stack of vintage envelopes"
        />
        <img
          className={`${styles.mailDecoration} ${styles.mailPhish}`}
          src="/assets/v5-mail-phish.png"
          width={1268}
          height={619}
          alt="Envelope stamped PHISH"
        />
        <img
          className={`${styles.mailDecoration} ${styles.mailSpam}`}
          src="/assets/v5-mail-spam.png"
          width={1319}
          height={665}
          alt="Envelope stamped SPAM"
        />
        <img
          className={styles.sourceNotebook}
          src="/assets/v5-open-source-notebook.png"
          width={1536}
          height={1024}
          alt="Open pixel-art notebook reading Open Source Is Cool"
        />
        <div
          className={`${styles.sourceHint} ${hintVisible ? styles.sourceHintVisible : ''}`}
          aria-hidden={!hintVisible}
        >
          <span className={styles.computerCallout}>
            USE CRT TABS
            <br />
            <b>CONDUIT · NEXT-PHISH</b>
          </span>
        </div>
      </div>
    </section>
  );
}
